// ============================================================================
// ui/haptics.js — a small, honest haptics layer
// ============================================================================
// Ryan 2026-08-09: *"add 50 ms haptics on all mobile buttons/actions… as each [colour] populates, a
// little subtle haptics thump… want to provide a premium experience for all users, even those on
// Android — and perhaps eventually iOS catches up."*
//
// ⛔ THIS IS ANDROID-ONLY, AND THAT IS NOT A BUG TO FIX LATER. `navigator.vibrate` is the Vibration
// API; iOS Safari does not implement it and no shim can — WebKit exposes no vibration primitive at
// all, so there is nothing to fall back to. Every call here is a no-op on iPhone, on desktop, and on
// any Android where the user has silenced vibration. The feature therefore has to be pure garnish:
// nothing may DEPEND on a buzz having happened.
//
// ⚠ COARSE POINTER ONLY. Desktop Chrome ships `navigator.vibrate` and it does nothing (no hardware),
// so calling it there is harmless but pointless — and on a touch LAPTOP (Ryan's own machine reports
// a coarse pointer) it is equally pointless. The gate is "does this device have vibration hardware
// we can reach", which in practice is `vibrate` present AND a coarse pointer.
//
// ⚠ AND IT IS RATE-LIMITED. Eight colour chips landing 720ms apart is a pleasant sequence; the same
// eight arriving in one frame is a buzz that reads as a malfunction. `MIN_GAP` swallows anything too
// close behind the last pulse, so no caller has to think about it.
// ============================================================================

const MIN_GAP = 40;   // ms; below this the previous pulse has not finished being felt
let last = 0;
let enabled = true;

/** One place that answers "can this device do this at all". */
function usable() {
  if (!enabled) return false;
  try {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return false;
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * @param {number|number[]} pattern  ms, or an on/off/on pattern.
 * @param {{force?: boolean}} [opts] `force` skips the rate limit — for a one-off moment (the reveal)
 *   that must not be swallowed because a button happened to be pressed just before it.
 */
export function haptic(pattern, { force = false } = {}) {
  if (!usable()) return false;
  const now = Date.now();
  if (!force && now - last < MIN_GAP) return false;
  last = now;
  try { navigator.vibrate(pattern); return true; } catch { return false; }
}

/* ── HOW HARD, AND WHY NOT ALL THE SAME (Ryan 2026-08-09: *"if you think 50 is too much, we can
   also go down… or scale it depending on the action — that was just the first number that came to
   mind because I know it works"*) ─────────────────────────────────────────────────────────────────
   50ms is a firm, unmistakable pulse. It is right for a decision and too much for a rummage: on a
   screen where a buyer taps six category cards in a row it stops reading as feedback and starts
   reading as the phone objecting. So the scale follows the WEIGHT of the action, not the widget:

     14ms  something small ARRIVED that you did not press — one colour chip landing
     18ms  an ordinary press — chips, tabs, tool buttons
     30ms  a thrown thing LANDING — the ghost reaching the switcher bar
     50ms  a press that changes the ORDER — the ± on a pill, Next, Design My Weights, Submit
     45ms  the reveal, once per design

   ⭐ THE PRESS/LAND PAIR IS RYAN'S, and it is the most interesting thing in this table (2026-08-09:
   *"when you click the '+' on pick weights tab, it could be 50 on click, and 30 on land when the
   image flies to the bar"*). Two pulses for one gesture, the second lighter than the first: it reads
   as throw-then-catch, and the drop in strength is what stops the pair feeling like a stutter. It is
   also the only place in the app where one action is felt twice, which is what makes it special
   rather than noisy — the ~500ms of flight between them is doing the work.

   ⚠ 50 is Ryan's own number and it sits at the TOP of the scale, not across all of it. It is right
   for a decision and too much for a rummage: on a screen where a buyer taps six category cards in a
   row, 50 stops reading as feedback and starts reading as the phone objecting. */

/** An ordinary press. */
export const tap = () => haptic(18);
/** A press that changes what is being ordered. */
export const press = () => haptic(50);
/** Something small arrived on its own — a colour chip. */
export const tick = () => haptic(14);
/** A thrown thing arriving. Lighter than the press that threw it, deliberately. */
export const land = () => haptic(30);
/** A moment worth feeling: the design revealed. */
export const thump = () => haptic(45, { force: true });
/** Something completed and sent. */
export const success = () => haptic([16, 45, 28], { force: true });

/** Global off switch, for a settings toggle or a test harness. */
export function setHapticsEnabled(on) { enabled = !!on; }

/**
 * Wire EVERY button in one place.
 *
 * ⭐ ONE DELEGATED LISTENER, not a call at each site. There are well over a hundred buttons in this
 * app across a dozen modules; touching each one would be the bloat Ryan asked to avoid, and the
 * first new button anyone adds would silently miss out. Delegation makes "all mobile buttons" true
 * by construction rather than by diligence.
 *
 * ⛔ IT FIRES ON RELEASE, NOT ON PRESS, AND THAT IS A BUG FIX RATHER THAN A PREFERENCE. `pointerdown`
 * is the obvious choice — feedback at the moment the finger lands — and it is wrong on a touch
 * screen for a reason no amount of tuning fixes: at pointerdown the browser does not YET KNOW
 * whether this is a press or the first frame of a scroll. Ryan, scrolling the welcome screen with a
 * finger that happened to start on a Select button: *"if my finger clicks and swipes on the 'Select
 * weight' button (but doesn't actually select it) the haptics will go off."* Exactly so — and a
 * vibration cannot be recalled once sent, so there is no cancel-on-move version of this.
 *   So the press is REMEMBERED at pointerdown and only PAID OUT at pointerup, if the gesture turned
 * out to be a tap: little movement, still over the same button, and not cancelled by the scroller
 * taking the pointer away. On a real tap those two events are ~80ms apart, which is under the
 * threshold where a person reads feedback as "late" — the standard tap heuristic, and the same one
 * the platform itself uses to decide whether to emit a click.
 *
 * ⚠ CAPTURE PHASE, so a handler that calls `stopPropagation` (the ± inside a pill does exactly that)
 * cannot rob its own button of feedback.
 * ⚠ Disabled controls stay silent: a buzz is a promise something happened, and nothing did.
 */
export function installButtonHaptics(root = document) {
  const SLOP = 10;      // px of travel still counted as a tap rather than a drag
  const MAX_HOLD = 900; // ms; beyond this it is a long-press or a stalled scroll, not a tap
  const BTN = 'button, [role="button"], a.wd-btn';
  let armed = null;
  let x0 = 0;
  let y0 = 0;
  let t0 = 0;

  const disarm = () => { armed = null; };

  root.addEventListener('pointerdown', (e) => {
    disarm();
    if (!usable()) return;
    const el = e.target instanceof Element ? e.target.closest(BTN) : null;
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true') return;
    armed = el; x0 = e.clientX; y0 = e.clientY; t0 = Date.now();
  }, { capture: true, passive: true });

  // The scroller has taken the gesture — it was never a press.
  root.addEventListener('pointercancel', disarm, { capture: true, passive: true });

  root.addEventListener('pointerup', (e) => {
    const el = armed;
    disarm();
    if (!el || !usable()) return;
    if (Date.now() - t0 > MAX_HOLD) return;
    if (Math.hypot(e.clientX - x0, e.clientY - y0) > SLOP) return;      // it was a swipe
    // …and the finger has to come up on the SAME control it went down on.
    const upOn = e.target instanceof Element ? e.target.closest(BTN) : null;
    if (upOn !== el) return;
    /* The heavier pulse is for buttons that change what is being ORDERED — the ± on a pill, the
       gate's CTA, the who-card's build button, the quote submit. Matched on the classes those
       already carry rather than on a list of ids, so a new primary button inherits the right weight
       without being registered here. */
    const big = el.matches('.wd-cfg__btn, .wd-btn--primary, .wd-wtf-go, .wd-gate-resume, .wd-quote-btn, .wd-submit, [type="submit"]');
    if (big) press(); else tap();
  }, { capture: true, passive: true });
}
